import React from "react";
import Skeleton from "../Skeleton/Skeleton";
import "../../containers/CreatorAnalytics/CreatorAnalytics.scss";
import "../../containers/CreatorAnalytics/CreationsAnalytics.scss";
import "../../containers/CreatorAnalytics/NewsAnalytics.scss";
import "../ModifyCreation/ModifyCreation.scss";
import "../PaiementTable/PaiementTable.scss";

/** Full-screen session / route bootstrap (App protected routes). */
export function SessionBootstrapSkeleton() {
    return (
        <div
            className="d-flex f-column aic jcc"
            style={{ minHeight: "100dvh", width: "100%", background: "#fff" }}
            role="status"
            aria-label="Loading"
        >
            <Skeleton width="120px" height="120px" borderRadius="24px" className="m-0 p-0 mb-24" />
            <Skeleton width="200px" height="14px" borderRadius="8px" className="m-0 p-0 mb-12" />
            <Skeleton width="160px" height="14px" borderRadius="8px" className="m-0 p-0" />
        </div>
    );
}

/** Profile “Other” role while balance loads. */
export function ProfileBalanceSkeleton() {
    return (
        <div className="action-container p-16" role="status" aria-label="Loading balance">
            <Skeleton width="100%" height="140px" borderRadius="16px" className="m-0 p-0 mb-32" />
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="d-flex aic mb-16 pb-16" style={{ borderBottom: "1px solid #eee" }}>
                    <Skeleton width="48px" height="48px" borderRadius="12px" className="m-0 p-0 mr-16" />
                    <div style={{ flex: 1 }}>
                        <Skeleton width="55%" height="16px" borderRadius="8px" className="m-0 p-0 mb-8" />
                        <Skeleton width="75%" height="12px" borderRadius="6px" className="m-0 p-0" />
                    </div>
                </div>
            ))}
        </div>
    );
}

/** Creator analytics “Overall” tab dashboard. */
export function CreatorAnalyticsDashboardSkeleton() {
    return (
        <div className="creator-analytics" role="status" aria-label="Loading analytics">
            <div className="creator-analytics__content p-16">
                <div className="creator-analytics__tabs" aria-hidden>
                    {[1, 2, 3].map((k) => (
                        <div key={k} style={{ flex: 1, display: "flex", justifyContent: "center", padding: "14px 8px" }}>
                            <Skeleton width="80%" height="18px" borderRadius="6px" className="m-0 p-0" />
                        </div>
                    ))}
                </div>
                <div className="creator-analytics__stats-grid">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="creator-analytics__stat-card">
                            <Skeleton width="60%" height="14px" borderRadius="6px" className="m-0 p-0 mb-8" />
                            <Skeleton width="48px" height="28px" borderRadius="8px" className="m-0 p-0" />
                        </div>
                    ))}
                </div>
                <Skeleton width="100%" height="220px" borderRadius="12px" className="m-0 p-0 mb-16" />
                <Skeleton width="100%" height="180px" borderRadius="12px" className="m-0 p-0" />
            </div>
        </div>
    );
}

/** Creations / News subtabs list with search. */
export function AnalyticsSubtabListSkeleton({ variant = "creations" }) {
    const isNews = variant === "news";
    const root = isNews ? "news-analytics" : "creations-analytics";
    const thumbW = isNews ? "142px" : "187.571px";

    return (
        <div className={root} role="status" aria-label="Loading list">
            <div className={`${root}__toolbar`}>
                <Skeleton width="100%" height="40px" borderRadius="8px" className="m-0 p-0" />
            </div>
            <ul className={`${root}__list`} aria-hidden>
                {[1, 2, 3, 4, 5].map((i) => (
                    <li key={i} className={`${root}__item`}>
                        <div className={`${root}__card`} style={{ pointerEvents: "none" }}>
                            <Skeleton
                                width={thumbW}
                                height={thumbW}
                                borderRadius="8px"
                                className="m-0 p-0"
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Skeleton width="70%" height="16px" borderRadius="8px" className="m-0 p-0 mb-8" />
                                <Skeleton width="40%" height="14px" borderRadius="7px" className="m-0 p-0 mb-8" />
                                <Skeleton width="50%" height="12px" borderRadius="6px" className="m-0 p-0" />
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/** Admin detail pages (news / creation / user). */
export function AdminDetailPageSkeleton({ className = "" }) {
    return (
        <div className={className} role="status" aria-label="Loading">
            <div className="p-16">
                <Skeleton width="70%" height="24px" borderRadius="8px" className="m-0 p-0 mb-24" />
                <div className="d-flex f-row flex-wrap gap-12 mb-24" style={{ gap: 12 }}>
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} width="140px" height="88px" borderRadius="12px" className="m-0 p-0" />
                    ))}
                </div>
                <Skeleton width="100%" height="200px" borderRadius="12px" className="m-0 p-0 mb-16" />
                <Skeleton width="100%" height="120px" borderRadius="12px" className="m-0 p-0" />
            </div>
        </div>
    );
}

/** MainEdit when creation not yet available. */
export function ModifyEditScreenSkeleton() {
    return (
        <div className="modify-container" role="status" aria-label="Loading editor">
            <div className="d-flex aic jcc modify-header mb-8 p-relative">
                <Skeleton width="80px" height="32px" borderRadius="8px" className="m-0 p-0" />
            </div>
            <div className="mt-40 pt-40 w-full px-16">
                <Skeleton width="100%" height="248px" borderRadius="12px" className="m-0 p-0 mb-24" />
                {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="d-flex aic mb-16">
                        <Skeleton width="100px" height="14px" borderRadius="6px" className="m-0 p-0 mr-16" />
                        <Skeleton width="60%" height="14px" borderRadius="6px" className="m-0 p-0" />
                    </div>
                ))}
            </div>
        </div>
    );
}

/** Admin payment table loading. */
export function HykTableLoadingSkeleton() {
    return (
        <div className="hyk-table" role="status" aria-label="Loading payments">
            <div className="table-header">
                <div className="search-box">
                    <Skeleton width="100%" height="40px" borderRadius="4px" className="m-0 p-0" />
                </div>
                <Skeleton width="120px" height="36px" borderRadius="4px" className="m-0 p-0" />
            </div>
            <div className="mobile-payments-list">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="mobile-table-card">
                        <div className="card-header">
                            <Skeleton width="40%" height="14px" borderRadius="6px" className="m-0 p-0" />
                            <Skeleton width="72px" height="24px" borderRadius="12px" className="m-0 p-0" />
                        </div>
                        <div className="card-content">
                            {[1, 2, 3].map((j) => (
                                <div key={j} className="content-row">
                                    <Skeleton width="64px" height="12px" borderRadius="4px" className="m-0 p-0" />
                                    <Skeleton width="55%" height="12px" borderRadius="4px" className="m-0 p-0" />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/** Heatmap initial load (StepperMap). */
export function HeatmapBootstrapSkeleton() {
    return (
        <div
            className="d-flex f-column aic jcc"
            style={{ minHeight: "72dvh", width: "100%" }}
            role="status"
            aria-label="Loading heatmap"
        >
            <Skeleton width="90%" height="360px" borderRadius="16px" className="m-0 p-0" />
            <Skeleton width="60%" height="16px" borderRadius="8px" className="m-0 p-0 mt-24" />
        </div>
    );
}

/** Generic stacked form placeholders (register, recover, complete profile, etc.). */
export function AuthFormStackSkeleton({ fields = 4 }) {
    return (
        <div className="px-16 py-24 w-full" style={{ maxWidth: 400, margin: "0 auto" }} role="status" aria-label="Loading">
            {Array.from({ length: fields }, (_, i) => (
                <Skeleton
                    key={i}
                    width="100%"
                    height="48px"
                    borderRadius="12px"
                    className="m-0 p-0 mb-16"
                />
            ))}
            <Skeleton width="100%" height="52px" borderRadius="12px" className="m-0 p-0 mt-8" />
        </div>
    );
}

/** CompleteProfileV2 — matches red full-screen step layout. */
export function CompleteProfileV2Skeleton() {
    return (
        <div
            className="d-flex f-column aic bg-red p-fixed"
            style={{ height: "100%", width: "100dvw", top: 0, left: 0 }}
            role="status"
            aria-label="Loading"
        >
            <div className="complete-profile-container pt-40 mt-40 auth-bg w-full px-16">
                <Skeleton width="220px" height="20px" borderRadius="8px" className="m-0 p-0 mx-auto mb-32 mt-12" />
                <Skeleton width="100%" height="52px" borderRadius="8px" className="m-0 p-0 mb-24" />
                <Skeleton width="100%" height="52px" borderRadius="12px" className="m-0 p-0 mt-40" />
            </div>
        </div>
    );
}
